// Contrats de maintenance (option « contrats_maintenance », lot 1 du 01/11/2026) : référentiels communs au serveur et à l'interface.
// Décisions du dirigeant (docs/CDC-CONTRATS-MAINTENANCE.md § 9, D1 à D9) : valeurs PAR DÉFAUT, modifiables contrat par contrat.
// Les valeurs réglementaires ne sont pas ici : shared/contrats/reglementaire.ts (fichier unique, daté et sourcé).

export const NATURES_CLIENT = ['professionnel', 'collectivite', 'petit_pro_hors_etab', 'syndicat_copro', 'particulier'] as const;
export type NatureClient = typeof NATURES_CLIENT[number];
export const LIBELLES_NATURE_CLIENT: Record<NatureClient, string> = {
  professionnel: 'Entreprise (professionnel)',
  collectivite: 'Collectivité',
  petit_pro_hors_etab: 'Très petite entreprise (5 salariés au plus) démarchée hors de ses locaux',
  syndicat_copro: 'Syndicat de copropriétaires',
  particulier: 'Particulier',
};

/** Modèles de contrat (décision D6). Le modèle « particulier » arrive au lot 5 (décision D8). */
export const SEGMENTS = ['entreprise_collectivite', 'syndicat_copro'] as const;
export type Segment = typeof SEGMENTS[number];
export const LIBELLES_SEGMENT: Record<Segment, string> = { entreprise_collectivite: 'Entreprise ou collectivité', syndicat_copro: 'Syndicat de copropriétaires' };

/** Segment du modèle déduit de la nature du client ; null = pas encore disponible (particulier : lot 5). */
export function segmentDe(nature: NatureClient): Segment | null {
  if (nature === 'syndicat_copro') return 'syndicat_copro';
  if (nature === 'particulier') return null;
  return 'entreprise_collectivite';
}
/** Le client est-il un « non-professionnel » (règles de reconduction L215 et clauses abusives) ? */
export const nonProfessionnel = (n: NatureClient) => n === 'syndicat_copro' || n === 'particulier';

export const LIEUX_CONCLUSION = ['etablissement', 'hors_etablissement', 'distance'] as const;
export type LieuConclusion = typeof LIEUX_CONCLUSION[number];
export const LIBELLES_LIEU_CONCLUSION: Record<LieuConclusion, string> = {
  etablissement: 'Dans les locaux de la société', hors_etablissement: 'Hors établissement (chez le client, démarchage)', distance: 'À distance (courriel, téléphone)',
};

export const STATUTS_CONTRAT = ['brouillon', 'signe', 'en_cours', 'a_renouveler', 'resilie', 'termine', 'remplace'] as const;
export type StatutContrat = typeof STATUTS_CONTRAT[number];
export const LIBELLES_STATUT_CONTRAT: Record<StatutContrat, string> = {
  brouillon: 'Brouillon', signe: 'Signé', en_cours: 'En cours', a_renouveler: 'À renouveler', resilie: 'Résilié', termine: 'Terminé',
  remplace: 'Remplacé par un avenant',
};

export const BRIQUES = ['preventif', 'telerevue', 'correctif_mo', 'pieces', 'telesurveillance', 'astreinte', 'bacs', 'cvc'] as const;
export type Brique = typeof BRIQUES[number];
export const LIBELLES_BRIQUE: Record<Brique, string> = {
  preventif: 'Visites préventives sur site', telerevue: 'Revues à distance de la supervision', correctif_mo: 'Dépannages (main-d’œuvre)',
  pieces: 'Pièces comprises', telesurveillance: 'Télésurveillance des alarmes', astreinte: 'Astreinte (nuit et week-end)',
  bacs: 'Option « Conformité BACS »', cvc: 'Option « CVC / fluides »',
};

/** Exclusions cochées (liste contrôlée) : ce qui n'est pas coché au périmètre est hors contrat dans tous les cas. */
export const EXCLUSIONS = ['securite_incendie', 'generateurs_cvc', 'licences_fabricants', 'pieces', 'extensions', 'inspections_reglementaires'] as const;
export type Exclusion = typeof EXCLUSIONS[number];
export const LIBELLES_EXCLUSION: Record<Exclusion, string> = {
  securite_incendie: 'Sécurité incendie (simples reports en lecture)', generateurs_cvc: 'Générateurs de chaud et de froid (sauf option CVC)',
  licences_fabricants: 'Licences et abonnements des fabricants', pieces: 'Pièces et fournitures', extensions: 'Extensions et modifications de l’installation',
  inspections_reglementaires: 'Inspections réglementaires (BACS, climatisation > 70 kW)',
};

export const PERIODICITES_FACTURATION = ['mensuelle', 'trimestrielle', 'semestrielle', 'annuelle'] as const;
export const MODES_FACTURATION = ['terme_echu', 'a_echoir'] as const;
export const LIBELLES_PERIODICITE_FACTURATION: Record<typeof PERIODICITES_FACTURATION[number], string> = {
  mensuelle: 'Mensuelle', trimestrielle: 'Trimestrielle', semestrielle: 'Semestrielle', annuelle: 'Annuelle',
};
export const LIBELLES_MODE_FACTURATION: Record<typeof MODES_FACTURATION[number], string> = { terme_echu: 'À terme échu', a_echoir: 'D’avance (à échoir)' };

// ─── Délais d'intervention (décision D1) ───
export const PRIORITES = ['P1', 'P2', 'P3'] as const;
export type Priorite = typeof PRIORITES[number];
export const LIBELLES_PRIORITE: Record<Priorite, string> = {
  P1: 'P1 — urgent : perte d’une fonction critique ou supervision aveugle',
  P2: 'P2 — important : dérive de confort ou d’énergie sur une zone',
  P3: 'P3 — mineur : gêne mineure, évolution',
};
/** Heures ouvrées de référence du contrat : du lundi au vendredi hors jours fériés, 8 h – 12 h et 14 h – 18 h (8 h par jour ouvré). */
export const MINUTES_PAR_JOUR_OUVRE = 8 * 60;
export const HORAIRES_REFERENCE = { jours: 'du lundi au vendredi, hors jours fériés', plages: ['08:00-12:00', '14:00-18:00'] };

export interface NiveauService {
  priorite: Priorite;
  /** Prise en charge à distance (minutes ouvrées) — obligation de résultat. */
  delai_distance_min: number | null;
  /** Arrivée sur site (minutes ouvrées) — obligation de résultat. */
  delai_site_min: number | null;
  /** Remise en service : null = jamais garantie (obligation de moyens, décision D1). */
  delai_retablissement_min: number | null;
  calendrier: 'ouvre' | 'continu';
  /** P3 : « à la prochaine visite ou sous N jours ouvrés ». */
  prochaine_visite: boolean;
}
/** Décision D1 du 01/11/2026 (valeurs par défaut, modifiables contrat par contrat). Le 24 h sur 24 n'existe qu'avec l'astreinte. */
export const NIVEAUX_D1: NiveauService[] = [
  { priorite: 'P1', delai_distance_min: 4 * 60, delai_site_min: 1 * MINUTES_PAR_JOUR_OUVRE, delai_retablissement_min: null, calendrier: 'ouvre', prochaine_visite: false },
  { priorite: 'P2', delai_distance_min: 1 * MINUTES_PAR_JOUR_OUVRE, delai_site_min: 3 * MINUTES_PAR_JOUR_OUVRE, delai_retablissement_min: null, calendrier: 'ouvre', prochaine_visite: false },
  { priorite: 'P3', delai_distance_min: null, delai_site_min: 10 * MINUTES_PAR_JOUR_OUVRE, delai_retablissement_min: null, calendrier: 'ouvre', prochaine_visite: true },
];

/** « 4 h ouvrées », « 1 jour ouvré », « 3 jours ouvrés » (calendrier ouvré : 8 h = 1 jour ouvré) ; « 4 h » en continu. */
export function texteDelai(min: number | null, calendrier: 'ouvre' | 'continu'): string | null {
  if (min === null || min === undefined) return null;
  if (calendrier === 'ouvre' && min % MINUTES_PAR_JOUR_OUVRE === 0) {
    const j = min / MINUTES_PAR_JOUR_OUVRE;
    return `${j} jour${j > 1 ? 's' : ''} ouvré${j > 1 ? 's' : ''}`;
  }
  const h = Math.floor(min / 60); const m = min % 60;
  const t = `${h ? `${h} h` : ''}${m ? `${h ? ' ' : ''}${m} min` : ''}` || '0 min';
  return calendrier === 'ouvre' ? `${t} ouvrées` : `${t} (24 h sur 24)`;
}

// ─── Formules par défaut (décision D7 : renommables, sans prix) ───
export interface FormuleDefaut {
  nom: string; briques: Brique[]; niveau_max: number; visites_par_an: number; telerevues_par_an: number;
  interventions_distance_incluses: number | null; interventions_site_incluses: number | null; exclusions: Exclusion[];
  /** Visites d'été et d'hiver : mois imposés (1 à 12), sinon répartition régulière. */
  mois_visites: number[] | null; description: string;
}
export const FORMULES_D7: FormuleDefaut[] = [
  { nom: 'Essentiel', briques: ['preventif', 'telerevue'], niveau_max: 2, visites_par_an: 1, telerevues_par_an: 1,
    interventions_distance_incluses: 0, interventions_site_incluses: 0, exclusions: ['securite_incendie', 'generateurs_cvc', 'licences_fabricants', 'pieces', 'extensions', 'inspections_reglementaires'],
    mois_visites: null, description: '1 visite par an, 1 revue à distance par an, sauvegarde des programmes, rapport signé.' },
  { nom: 'Confort', briques: ['preventif', 'telerevue', 'correctif_mo'], niveau_max: 3, visites_par_an: 2, telerevues_par_an: 4,
    interventions_distance_incluses: null, interventions_site_incluses: 0, exclusions: ['securite_incendie', 'generateurs_cvc', 'licences_fabricants', 'pieces', 'extensions', 'inspections_reglementaires'],
    mois_visites: null, description: '2 visites par an, revue à distance trimestrielle, dépannage à distance compris en main-d’œuvre.' },
  { nom: 'Sérénité', briques: ['preventif', 'telerevue', 'correctif_mo'], niveau_max: 3, visites_par_an: 4, telerevues_par_an: 12,
    interventions_distance_incluses: null, interventions_site_incluses: null, exclusions: ['securite_incendie', 'generateurs_cvc', 'licences_fabricants', 'pieces', 'extensions', 'inspections_reglementaires'],
    mois_visites: [1, 4, 7, 10], description: '4 visites par an dont une d’été et une d’hiver, revue mensuelle, dépannage à distance et sur site compris en main-d’œuvre, astreinte en option.' },
];

/** Nombre de visites par an accepté sans mois imposés : réparties régulièrement (diviseurs de 12). */
export const VISITES_PAR_AN_REGULIERES = [1, 2, 3, 4, 6, 12] as const;

// ─── Visites ───
export const STATUTS_VISITE = ['a_planifier', 'planifiee', 'en_cours', 'realisee', 'annulee'] as const;
export type StatutVisite = typeof STATUTS_VISITE[number];
export const LIBELLES_STATUT_VISITE: Record<StatutVisite, string> = {
  a_planifier: 'À planifier', planifiee: 'Planifiée', en_cours: 'En cours', realisee: 'Réalisée (rapport signé)', annulee: 'Annulée',
};
export const MODES_VISITE = ['site', 'distance'] as const;
export const LIBELLES_MODE_VISITE: Record<typeof MODES_VISITE[number], string> = { site: 'Visite sur site', distance: 'Revue à distance' };

export const FAMILLES_GAMME = ['supervision', 'automate', 'reseau_knx', 'reseau_bacnet', 'reseau_modbus', 'reseau_dali', 'reseau_mbus', 'reseau_lon',
  'passerelle', 'capteur', 'actionneur', 'comptage', 'cyber', 'documentation'] as const;
export type FamilleGamme = typeof FAMILLES_GAMME[number];
export const LIBELLES_FAMILLE_GAMME: Record<FamilleGamme, string> = {
  supervision: 'Supervision et serveur', automate: 'Automates et régulateurs', reseau_knx: 'Réseau KNX', reseau_bacnet: 'Réseau BACnet',
  reseau_modbus: 'Réseau Modbus', reseau_dali: 'Réseau DALI', reseau_mbus: 'Réseau M-Bus', reseau_lon: 'Réseau LON', passerelle: 'Passerelles',
  capteur: 'Capteurs', actionneur: 'Actionneurs', comptage: 'Comptage d’énergie', cyber: 'Cybersécurité (recommandation ANSSI)', documentation: 'Documentation',
};
export const TYPES_RELEVE = ['valeur', 'ok_ko', 'photo', 'fichier', 'texte', 'version'] as const;
export type TypeReleve = typeof TYPES_RELEVE[number];
export const PERIODICITES_ITEM = ['visite', 'annuelle', 'biennale', 'evenement'] as const;
export type PeriodiciteItem = typeof PERIODICITES_ITEM[number];
export const LIBELLES_PERIODICITE_ITEM: Record<PeriodiciteItem, string> = {
  visite: 'À chaque visite', annuelle: 'Une fois par an', biennale: 'Tous les deux ans', evenement: 'Après une modification',
};
export const RESULTATS_RELEVE = ['ok', 'ko', 'sans_objet'] as const;
export type ResultatReleve = typeof RESULTATS_RELEVE[number];
export const LIBELLES_RESULTAT_RELEVE: Record<ResultatReleve, string> = { ok: 'OK', ko: 'KO', sans_objet: 'Sans objet' };
export const GRAVITES_ECART = ['mineure', 'majeure', 'critique'] as const;
export const STATUTS_ECART = ['ouvert', 'devis', 'traite', 'accepte_en_l_etat'] as const;
export const LIBELLES_STATUT_ECART: Record<typeof STATUTS_ECART[number], string> = {
  ouvert: 'Ouvert', devis: 'Devis à faire', traite: 'Traité', accepte_en_l_etat: 'Accepté en l’état par le client',
};

/** Mention discrète en pied de chaque modèle (décision D6 : TRAME est un logiciel, pas un conseil juridique). */
export const PIED_DE_MODELE = 'Modèle fourni par TRAME : à faire valider par votre conseil avant la première utilisation.';

/** Forme d'un contrat lue par le technicien (aucun prix, aucun montant). */
export interface ContratPourVisite { id: string; reference: string; intitule: string; formule: string | null }
